import { Aide } from '@/components/ui/aide';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Plus, RotateCcw, Save, Trash2 } from 'lucide-react';
import { orgSettingsAPI, organizationsAPI } from '../lib/api';
import { organizationOption } from '../lib/selectOptions';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Tabs } from '../components/ui/tabs';
import { TabsList } from '../components/ui/tabs-list';
import { TabsTrigger } from '../components/ui/tabs-trigger';
import { TabsContent } from '../components/ui/tabs-content';
import { PageHeader } from '../components/ui/page-header';
import { LoadingState } from '../components/ui/loading-state';
import { messageErreur } from '../lib/erreurs';
import { libelle, texteSur } from '../lib/libelles';

//: Champs dont la saisie est un texte libre tant que l'admin tape, converti
//: seulement à l'enregistrement (voir `toPayload`).
const NUMBER_FIELDS = new Set([
  'demurrage_max_hours',
  'volume_tolerance_percent',
  'volume_tolerance_liters',
  'consumption_tolerance_percent',
  'geofence_min_gps_tolerance_meters',
  'geofence_max_gps_tolerance_meters',
  'geofence_radius_tolerance_ratio',
  'geofence_exit_hysteresis_min_meters',
  'max_concurrent_trips_per_truck',
  'delivery_settlement_max_days',
]);

const LIST_FIELDS = new Set(['critical_checklist_keys']);

const RELEASE_PHASE_LABELS = {
  BORDER_CROSSED: 'Dès la douane d’entrée franchie',
  DELIVERY_PHASE: 'Dès le passage en livraison',
  COMPLETED: 'À la clôture du trajet',
};

//: Le défaut du produit. Il vit ici parce que le repli du sélecteur doit dire
//: la même chose que le serveur: laisser `DELIVERY_PHASE` en repli affichait
//: une phase que la société n'avait pas choisie, et qui n'était plus le défaut.
const RELEASE_PHASE_DEFAULT = 'BORDER_CROSSED';

/** Affiche d'où vient la valeur, pour que l'admin sache ce qu'il a réglé. */
const SourceBadge = ({ source }) =>
  source === 'org' ? (
    <Badge className="bg-info/15 text-info text-[10px]">Réglé</Badge>
  ) : (
    <Badge className="bg-muted text-muted-foreground text-[10px]">Défaut</Badge>
  );

const Field = ({ label, hint, source, onReset, children }) => {
  // Une aide de huit mots ou moins reste sous le champ; au-delà, elle passe
  // derrière une icône d'aide à côté du libellé.
  const hintCourt = typeof hint === 'string' && hint.trim().split(/\s+/).length <= 8;
  return (
  <div className="space-y-1.5">
    <div className="flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-1">
        <label className="text-sm font-semibold text-foreground">{label}</label>
        {hint && !hintCourt && <Aide titre={typeof label === 'string' ? label : 'Aide'}>{hint}</Aide>}
      </div>
      <div className="flex items-center gap-1">
        {source && <SourceBadge source={source} />}
        {source === 'org' && onReset && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-1 text-muted-foreground"
            onClick={onReset}
            title="Revenir à la valeur par défaut"
          >
            <RotateCcw className="h-3 w-3" aria-hidden="true" />
          </Button>
        )}
      </div>
    </div>
    {children}
    {hint && hintCourt && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
  );
};

const EntryListEditor = ({ entries, onChange }) => (
  <div className="space-y-2">
    {entries.map((entry, index) => (
      // La clé ne doit PAS contenir la valeur éditée: elle changerait à chaque
      // touche, React remonterait le champ et le focus serait perdu — le
      // symptôme exact d'un champ « qui se désactive » en cours de frappe.
      // eslint-disable-next-line react/no-array-index-key
      <div key={index} className="flex items-center gap-2">
        <Input
          value={entry.key}
          onChange={(e) => {
            const next = [...entries];
            next[index] = { ...entry, key: e.target.value.toUpperCase() };
            onChange(next);
          }}
          className="w-40 font-mono text-xs"
          placeholder="Code court"
        />
        <Input
          value={entry.label || ''}
          onChange={(e) => {
            const next = [...entries];
            next[index] = { ...entry, label: e.target.value };
            onChange(next);
          }}
          placeholder="Libellé affiché au chauffeur"
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-destructive"
          onClick={() => onChange(entries.filter((_, i) => i !== index))}
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    ))}
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => onChange([...entries, { key: '', label: '' }])}
    >
      <Plus className="h-4 w-4" aria-hidden="true" />
      Ajouter
    </Button>
  </div>
);

export default function OrgBlockingSettingsPage() {
  const { isSuperAdmin, refreshProfile } = useAuth();
  const [data, setData] = useState(null);
  const [gates, setGates] = useState(null);
  const [draft, setDraft] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const organizationOptions = useMemo(
    () =>
      organizations.map(organizationOption),
    [organizations],
  );

  useEffect(() => {
    if (!isSuperAdmin) return;
    organizationsAPI
      .getEvery()
      .then((rows) => {
        const next = Array.isArray(rows) ? rows : [];
        setOrganizations(next);
        setSelectedOrganizationId((current) => current || next[0]?.id || '');
      })
      .catch(() => toast.error('Impossible de charger les organisations'));
  }, [isSuperAdmin]);

  const load = useCallback(async () => {
    if (isSuperAdmin && !selectedOrganizationId) {
      setData(null);
      setGates(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const organizationId = isSuperAdmin ? selectedOrganizationId : undefined;
    const [policy, workflowGates] = await Promise.all([
      orgSettingsAPI.getBlockingPolicy(organizationId),
      orgSettingsAPI.getWorkflowGates(organizationId).catch(() => null),
    ]);
    setData(policy);
    setGates(workflowGates);
    setDraft({});
  }, [isSuperAdmin, selectedOrganizationId]);

  useEffect(() => {
    load()
      .catch(() => toast.error('Impossible de charger les réglages des blocages'))
      .finally(() => setIsLoading(false));
  }, [load]);

  const policy = data?.policy;
  const sources = data?.sources || {};

  // Le brouillon ne contient que ce que l'admin a touché: l'API traite un
  // champ absent comme « inchangé » et `null` comme « revenir au défaut ».
  const value = (field) => (field in draft ? draft[field] : policy?.[field]);
  const set = (field, next) => setDraft((prev) => ({ ...prev, [field]: next }));
  const reset = (field) => setDraft((prev) => ({ ...prev, [field]: null }));
  const sourceOf = (field) =>
    field in draft ? (draft[field] === null ? 'default' : 'org') : sources[field];

  const dirty = Object.keys(draft).length > 0;

  // Les deux phases qui libèrent le camion avant qu'il ait remis sa cargaison.
  // `BORDER_CROSSED` le libère encore plus tôt que `DELIVERY_PHASE`: l'avertir
  // seulement sur la seconde aurait laissé le cas le plus marqué sans un mot.
  const conflictingRelease = useMemo(
    () =>
      ['BORDER_CROSSED', 'DELIVERY_PHASE'].includes(
        value('truck_release_phase'),
      ) && value('depot_stop_required') === false,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft, policy],
  );

  // Le brouillon garde la frappe telle quelle et ne la convertit qu'à
  // l'enregistrement. Convertir à chaque touche rendait certains champs
  // impossibles à remplir: « 0. » redevenait « 0 », donc on ne pouvait pas
  // écrire 0,5; et la virgule d'une liste disparaissait sous les doigts.
  const toPayload = (currentDraft) =>
    Object.fromEntries(
      Object.entries(currentDraft).map(([field, raw]) => {
        if (raw === null || raw === '') return [field, null];
        if (NUMBER_FIELDS.has(field)) {
          const parsed = Number(String(raw).replace(',', '.'));
          return [field, Number.isFinite(parsed) ? parsed : null];
        }
        if (LIST_FIELDS.has(field) && typeof raw === 'string') {
          const items = raw
            .split(',')
            .map((item) => item.trim().toUpperCase())
            .filter(Boolean);
          return [field, items.length ? items : null];
        }
        return [field, raw];
      }),
    );

  const save = async () => {
    setIsSaving(true);
    try {
      const saved = await orgSettingsAPI.updateBlockingPolicy(
        toPayload(draft),
        isSuperAdmin ? selectedOrganizationId : undefined,
      );
      setData(saved);
      // Le réglage « dépôt » change l'accueil, le menu et la page Stock: le
      // profil est relu pour que l'écran suive sans reconnexion.
      const depotChange = 'manages_depot' in draft;
      setDraft({});
      toast.success('Réglages enregistrés');
      if (depotChange && !isSuperAdmin && refreshProfile) {
        refreshProfile().catch(() => {});
      }
    } catch (error) {
      toast.error(messageErreur(error, 'Impossible d’enregistrer les réglages'));
    } finally {
      setIsSaving(false);
    }
  };

  const saveGates = async (patch) => {
    try {
      const saved = await orgSettingsAPI.updateWorkflowGates(
        patch,
        isSuperAdmin ? selectedOrganizationId : undefined,
      );
      setGates(saved);
      toast.success('Garde-fous mis à jour');
    } catch (error) {
      toast.error(messageErreur(error, 'Impossible de mettre à jour les garde-fous'));
    }
  };

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 lg:px-8">
        <LoadingState shape="readouts" />
      </div>
    );
  }

  if (!policy) return null;

  const numberField = (field, label, hint, step = '1') => (
    <Field
      label={label}
      hint={hint}
      source={sourceOf(field)}
      onReset={() => reset(field)}
    >
      <Input
        type="number"
        step={step}
        value={value(field) ?? ''}
        onChange={(e) => set(field, e.target.value === '' ? null : e.target.value)}
      />
    </Field>
  );

  return (
    <div
      className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8"
      data-testid="org-blocking-settings-page"
    >
      <PageHeader
        eyebrow="Paramètres société"
        title="Blocages"
        description="Ce qui arrête une mission, et à partir de quel seuil."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {isSuperAdmin && (
              <SearchableSelect
                className="min-w-64"
                aria-label="Organisation à configurer"
                placeholder="Sélectionner une organisation"
                searchPlaceholder="Rechercher une organisation…"
                emptyMessage="Aucune organisation trouvée"
                value={selectedOrganizationId}
                onValueChange={setSelectedOrganizationId}
                options={organizationOptions}
              />
            )}
            <Button
              onClick={save}
              disabled={!dirty || isSaving || (isSuperAdmin && !selectedOrganizationId)}
            >
              <Save className="h-4 w-4" aria-hidden="true" />
              {isSaving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        }
      />

      <Tabs defaultValue="seuils">
        <TabsList>
          <TabsTrigger value="seuils">Seuils</TabsTrigger>
          <TabsTrigger value="parcours">Parcours</TabsTrigger>
          <TabsTrigger value="etapes">Étapes et contrôles</TabsTrigger>
          <TabsTrigger value="terrain">Motifs terrain</TabsTrigger>
          <TabsTrigger value="controles">Contrôles</TabsTrigger>
        </TabsList>

        <TabsContent value="seuils">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Délai douanier</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Field
                  label="Surveiller les stationnements en douane"
                  source={sourceOf('demurrage_enabled')}
                  onReset={() => reset('demurrage_enabled')}
                >
                  <Switch
                    checked={Boolean(value('demurrage_enabled'))}
                    onCheckedChange={(checked) => set('demurrage_enabled', checked)}
                  />
                </Field>
                {numberField(
                  'demurrage_max_hours',
                  'Délai avant blocage (heures)',
                  'Au-delà, le trajet est signalé bloqué à la douane.',
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Tolérances volume</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {numberField(
                  'volume_tolerance_liters',
                  'Tolérance en litres',
                  'Un écart doit dépasser les litres ET le pourcentage pour compter.',
                  '10',
                )}
                {numberField(
                  'volume_tolerance_percent',
                  'Tolérance en %',
                  null,
                  '0.1',
                )}
                {numberField(
                  'consumption_tolerance_percent',
                  'Tolérance consommation (%)',
                  'Écart accepté entre consommation théorique et réelle.',
                  '0.1',
                )}
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Seuils GPS</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {numberField(
                  'geofence_min_gps_tolerance_meters',
                  'Tolérance minimale (m)',
                )}
                {numberField(
                  'geofence_max_gps_tolerance_meters',
                  'Tolérance maximale (m)',
                )}
                {numberField(
                  'geofence_radius_tolerance_ratio',
                  'Part du rayon (ratio)',
                  null,
                  '0.01',
                )}
                {numberField(
                  'geofence_exit_hysteresis_min_meters',
                  'Marge de sortie (m)',
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="parcours">
          <Card>
            <CardHeader>
              <CardTitle>Parcours et disponibilité des camions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 md:max-w-xl">
              <Field
                label="La société gère un dépôt"
                hint="Désactivé, le carburant chargé est livré directement aux clients : pas de jauge ni d'alerte de stock bas, le stock est le carburant chargé et pas encore livré."
                source={sourceOf('manages_depot')}
                onReset={() => reset('manages_depot')}
              >
                <Switch
                  checked={value('manages_depot') !== false}
                  onCheckedChange={(checked) => set('manages_depot', checked)}
                  aria-label="La société gère un dépôt"
                />
              </Field>

              {value('manages_depot') !== false && (
              <Field
                label="Passage au dépôt obligatoire"
                hint="Désactivé, le camion peut livrer directement après la douane d'entrée."
                source={sourceOf('depot_stop_required')}
                onReset={() => reset('depot_stop_required')}
              >
                <Switch
                  checked={Boolean(value('depot_stop_required'))}
                  onCheckedChange={(checked) => set('depot_stop_required', checked)}
                />
              </Field>
              )}

              <Field
                label="Le camion redevient disponible"
                source={sourceOf('truck_release_phase')}
                onReset={() => reset('truck_release_phase')}
              >
                <Select
                  value={value('truck_release_phase') || RELEASE_PHASE_DEFAULT}
                  onValueChange={(next) => set('truck_release_phase', next)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(data.options?.truck_release_phases || []).map((phase) => (
                      <SelectItem key={phase} value={phase}>
                        {RELEASE_PHASE_LABELS[phase] || phase}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              {numberField(
                'max_concurrent_trips_per_truck',
                'Trajets simultanés par camion',
                'Un trajet passé en livraison ne compte plus comme mobilisant le camion.',
              )}

              {numberField(
                'delivery_settlement_max_days',
                'Signaler une livraison non soldée après (jours)',
                'Un camion libéré à la douane repart sans avoir remis sa cargaison : passé ce délai, la commande non soldée est signalée. 0 pour ne rien signaler.',
              )}

              {conflictingRelease && (
                <div className="flex gap-2 rounded-xl border border-flame/40 bg-flame/10 p-3 text-sm">
                  <AlertTriangle
                    className="mt-0.5 h-4 w-4 shrink-0 text-flame"
                    aria-hidden="true"
                  />
                  <p className="text-muted-foreground">
                    Avec ces deux réglages, un camion est déclaré disponible alors
                    qu’il roule encore avec sa cargaison.{' '}
                    <Aide titre="Camion disponible avec sa cargaison">
                      Le dépôt est sauté. C’est utile pour planifier la rotation
                      suivante, à condition que la répartition en tienne compte.
                    </Aide>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="etapes">
          <Card>
            <CardHeader>
              <CardTitle>Étapes du parcours</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(gates?.steps || []).map((step) => (
                <div
                  key={step.code}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border p-3"
                >
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      {libelle('tripStatus', step.code, texteSur(step.name, 'Étape du trajet'))}
                    </p>
                  </div>
                  <div className="flex items-center gap-5">
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                      Alerte prioritaire
                      <Switch
                        checked={step.is_blocking}
                        onCheckedChange={(checked) =>
                          saveGates({
                            steps: [{ code: step.code, is_blocking: checked }],
                          })
                        }
                      />
                    </label>
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                      Facultative
                      <Switch
                        checked={step.is_optional}
                        disabled={step.stock_critical}
                        onCheckedChange={(checked) =>
                          saveGates({
                            steps: [{ code: step.code, is_optional: checked }],
                          })
                        }
                      />
                    </label>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Pièces exigées par action</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(gates?.transitions || []).map((transition) => (
                <div
                  key={transition.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border p-3"
                >
                  {/* Chaque étape peut être annulée: sans l'étape d'origine,
                      la liste affichait treize « Annulé » indiscernables. */}
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      {texteSur(transition.action_label, 'Action')}
                    </p>
                    {transition.from_step_label && (
                      <p className="text-xs text-muted-foreground">
                        depuis {texteSur(transition.from_step_label, '')}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-5">
                    {[
                      ['requires_document', 'Document'],
                      ['requires_comment', 'Commentaire'],
                      ['requires_location', 'Position'],
                      ['requires_signature', 'Signature'],
                    ].map(([field, label]) => (
                      <label
                        key={field}
                        className="flex items-center gap-2 text-xs text-muted-foreground"
                      >
                        {label}
                        <Switch
                          checked={transition[field]}
                          onCheckedChange={(checked) =>
                            saveGates({
                              transitions: [
                                { id: transition.id, [field]: checked },
                              ],
                            })
                          }
                        />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="terrain">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Motifs de blocage au poste frontière</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-end">
                  <SourceBadge source={sourceOf('blockage_reasons')} />
                </div>
                <EntryListEditor
                  entries={value('blockage_reasons') || []}
                  onChange={(next) => set('blockage_reasons', next)}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Pièces à présenter</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-end">
                  <SourceBadge source={sourceOf('required_border_pieces')} />
                </div>
                <EntryListEditor
                  entries={value('required_border_pieces') || []}
                  onChange={(next) => set('required_border_pieces', next)}
                />
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Checklist de départ</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Field
                  label="Checklist obligatoire avant le départ"
                  hint="Le départ est refusé tant que tous les points critiques ne sont pas cochés."
                  source={
                    policy.departure_checklist_required ? 'org' : 'default'
                  }
                >
                  <Switch
                    checked={Boolean(value('departure_checklist_required'))}
                    onCheckedChange={(checked) =>
                      set('departure_checklist_required', checked)
                    }
                  />
                </Field>
                <Field
                  label="Points critiques"
                  source={sourceOf('critical_checklist_keys')}
                  onReset={() => reset('critical_checklist_keys')}
                >
                  <Input
                    value={
                      typeof value('critical_checklist_keys') === 'string'
                        ? value('critical_checklist_keys')
                        : (value('critical_checklist_keys') || []).join(', ')
                    }
                    onChange={(e) =>
                      set('critical_checklist_keys', e.target.value)
                    }
                    className="font-mono text-xs"
                    placeholder="Codes séparés par des virgules"
                  />
                </Field>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="controles">
          <Card>
            <CardHeader>
              <CardTitle>Contrôles de préparation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(data.options?.readiness_controls || []).map((key) => {
                const controls = value('readiness_controls') || {};
                return (
                  <div
                    key={key}
                    className="flex items-center justify-between gap-3 rounded-xl border border-border p-3"
                  >
                    <p className="text-sm font-semibold text-foreground">
                      {libelle('readinessControl', key)}
                    </p>
                    <Select
                      value={controls[key] === 'off' ? 'off' : 'watch'}
                      onValueChange={(next) =>
                        set('readiness_controls', { ...controls, [key]: next })
                      }
                    >
                      <SelectTrigger className="w-44">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {['watch', 'off'].map(
                          (severity) => (
                            <SelectItem key={severity} value={severity}>
                              {libelle('readinessSeverity', severity)}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
